import { ReactNode } from 'react';

import { Droppable } from '@hello-pangea/dnd';
import type { DroppableProvided, DroppableStateSnapshot } from '@hello-pangea/dnd';
import { InfoOutlined } from '@mui/icons-material';
import { Box, SxProps, Theme, Typography } from '@mui/material';

import { useLanguage } from '../../hooks';
import { SignatureOrderType, SignatureOrderUser, SignerData } from '../../types/taskForm';
import { DragAndDropSignerBox } from './DragAndDropSignerBox';
import { DragAndDropSignerGroupBox } from './DragAndDropSignerGroupBox';
import { generateGroupId, getDraggableId } from './utils';

interface SignatureOrderConfigProps {
  type: SignatureOrderType;
  isDragAndDropDisabled: boolean;
  isIncomplete?: boolean;
  order: SignatureOrderUser[][];
  usersData: Record<string, SignerData>;
  onSignerDelete: (signerId: string | number) => void;
  isCombineEnabled?: boolean;
  handleUnGroup?: (signerId: string | number, index: number) => void;
  sequentialInfo?: Record<string, { icon: JSX.Element; text: ReactNode }>;
  configState?: string;
}

const customStyles: Record<string, SxProps<Theme>> = {
  column: { width: '100%' },
  title: { fontSize: '20px', fontWeight: '500' },
  disabledTitle: {
    fontSize: '20px',
    fontWeight: '500',
    color: (theme) => theme.palette.text.disabled,
  },
  infoContainer: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    margin: '5px 0px 10px 0px',
  },
  infoText: { fontSize: '13px' },
  disabledInfoText: { color: (theme) => theme.palette.text.disabled, fontSize: '13px' },
  container: {
    padding: '10px',
    borderRadius: '12px',
    height: '400px',
    overflowY: 'auto',
    border: '1px solid transparent',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  draggingOverContainer: { border: (theme) => `1px dashed ${theme.palette.primary.main}` },
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateText: {
    fontSize: '16px',
    textAlign: 'center',
    color: (theme) => theme.palette.text.disabled,
    maxWidth: '258px',
  },
};

function EmptyDroppable({
  provided,
  snapshot,
  emptyText,
}: {
  provided: DroppableProvided;
  snapshot: DroppableStateSnapshot;
  emptyText: string;
}) {
  return (
    <Box
      ref={provided.innerRef}
      {...provided.droppableProps}
      sx={{ ...customStyles.container, ...customStyles.emptyStateContainer } as SxProps<Theme>}
    >
      <Box sx={{ display: 'none' }}>
        <span>{provided.placeholder}</span>
      </Box>
      <Typography
        sx={{
          ...customStyles.emptyStateText,
          color: (theme) =>
            snapshot.isDraggingOver ? theme.palette.primary.main : theme.palette.text.disabled,
        }}
      >
        {emptyText}
      </Typography>
    </Box>
  );
}

export function SignatureOrderConfig({
  type,
  isDragAndDropDisabled,
  isIncomplete = false,
  order,
  usersData,
  onSignerDelete,
  isCombineEnabled = false,
  handleUnGroup,
  sequentialInfo,
  configState,
}: SignatureOrderConfigProps) {
  const lang = useLanguage();

  const headerTitle =
    type === SignatureOrderType.SIMULTANEOUS
      ? lang.formSignatureBuilder.simultaneousSign
      : lang.formSignatureBuilder.sequentialSign;

  const headerIcon =
    type === SignatureOrderType.SIMULTANEOUS ? (
      <InfoOutlined sx={{ color: (theme) => theme.palette.primary.main, fontSize: '16px' }} />
    ) : configState && sequentialInfo ? (
      sequentialInfo[configState]?.icon ?? <InfoOutlined sx={{ fontSize: '16px' }} />
    ) : (
      <InfoOutlined sx={{ fontSize: '16px' }} />
    );

  const headerText =
    type === SignatureOrderType.SIMULTANEOUS
      ? lang.formSignatureBuilder.simultaneousSignInfo
      : configState && sequentialInfo
      ? sequentialInfo[configState]?.text
      : '';

  const emptyText =
    type === SignatureOrderType.SIMULTANEOUS
      ? lang.formSignatureBuilder.simultaneousEmptyState
      : isDragAndDropDisabled
      ? lang.formSignatureBuilder.optionalSignatureOrderDisabled
      : lang.formSignatureBuilder.sequentialEmptyState;

  return (
    <Box sx={customStyles.column}>
      <Typography sx={isDragAndDropDisabled ? customStyles.disabledTitle : customStyles.title}>
        {headerTitle}
      </Typography>
      <Box sx={customStyles.infoContainer}>
        {headerIcon}
        <Typography
          sx={isDragAndDropDisabled ? customStyles.disabledInfoText : customStyles.infoText}
        >
          {typeof headerText === 'string' ? headerText : <>{headerText}</>}
        </Typography>
      </Box>
      <Droppable
        droppableId={type}
        isDropDisabled={isDragAndDropDisabled}
        isCombineEnabled={type === SignatureOrderType.SEQUENTIAL ? isCombineEnabled : false}
      >
        {(provided, snapshot) => {
          const containerSx = {
            ...customStyles.container,
            ...(snapshot.isDraggingOver ? customStyles.draggingOverContainer : {}),
          } as SxProps<Theme>;

          if (order.length === 0) {
            return (
              <EmptyDroppable
                provided={provided}
                snapshot={snapshot}
                emptyText={emptyText}
              />
            );
          }

          if (type === SignatureOrderType.SIMULTANEOUS) {
            return (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={containerSx}
              >
                <Box sx={{ display: 'none' }}>
                  <span>{provided.placeholder}</span>
                </Box>
                {order.map((signerGroup, index) => (
                  <DragAndDropSignerBox
                    key={getDraggableId(signerGroup[0])}
                    signer={signerGroup}
                    index={index}
                    usersData={usersData}
                    onSignerDelete={onSignerDelete}
                    orderType={SignatureOrderType.SIMULTANEOUS}
                    isDragDisabled={isDragAndDropDisabled}
                    isIncomplete={isIncomplete}
                  />
                ))}
              </Box>
            );
          }

          return (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={containerSx}
            >
              <Box sx={{ display: 'none' }}>
                <span>{provided.placeholder}</span>
              </Box>
              {order.map((signers, index) => (
                <DragAndDropSignerGroupBox
                  key={generateGroupId(signers)}
                  signers={signers}
                  index={index}
                  isDragDisabled={isDragAndDropDisabled}
                  usersData={usersData}
                  onSignerDelete={onSignerDelete}
                  handleUnGroup={handleUnGroup}
                />
              ))}
            </Box>
          );
        }}
      </Droppable>
    </Box>
  );
}
